// #max-send-window — pure decisions for the "remember my skip-late-messages choice" default.
// The value is persisted on users.preferences.defaultMaxSendWindowMinutes at campaign launch and
// pre-fills the next new campaign. Extracted (not inline) so both decisions are unit-testable.

/**
 * What to pre-fill a FRESH campaign's window with, from the user's saved default.
 * Returns the minutes to apply, or null = apply nothing (leave the control off/untouched).
 *
 * @param timingHasKey  true if `maxSendWindowMinutes` already exists on the campaign's timing
 *   (present-with-undefined after an explicit uncheck) — never override the user's in-form choice.
 * @param isEditingExisting  true when editing/duplicating a campaign that already has its own timing.
 * @param userDefault  users.preferences.defaultMaxSendWindowMinutes (number | null | undefined).
 */
export function resolveWindowPrefill(
    timingHasKey: boolean,
    isEditingExisting: boolean,
    userDefault: number | null | undefined,
): number | null {
    if (timingHasKey || isEditingExisting) return null;
    if (typeof userDefault === 'number' && userDefault >= 1) return userDefault;
    return null;
}

/**
 * Whether to persist the just-launched window as the user's new default, and the value to write.
 * `null` = "off" (unchecked); persisting null lets an explicit uncheck become the new default.
 *
 * @param launchedWindowMinutes  the launched campaign's timing.maxSendWindowMinutes (or null/undefined).
 * @param storedDefault  the user's current preferences.defaultMaxSendWindowMinutes.
 */
export function resolveWindowToPersist(
    launchedWindowMinutes: number | null | undefined,
    storedDefault: number | null | undefined,
): { changed: boolean; value: number | null } {
    const value = typeof launchedWindowMinutes === 'number' && launchedWindowMinutes >= 1
        ? launchedWindowMinutes
        : null;
    const stored = typeof storedDefault === 'number' && storedDefault >= 1 ? storedDefault : null;
    return { changed: value !== stored, value };
}
